import { useEffect, useState } from 'react';
import { useStargateClient } from '@atlassiansox/bxpkit-core';
import { metalClient } from '../metrics';
import { catalog } from '@atlassiansox/metal-client';

export type SSOCheck = {
  readonly isCheckingSSO: boolean;
  readonly ssoRedirectUrl: string;
  readonly isUserExists?: boolean;
};

enum AuthenticationMethodsType {
  PASSWORD = 'password',
  GOOGLE = 'google',
  SAML = 'saml',
  MICROSOFT = 'microsoft',
  APPLE = 'apple',
  SLACK = 'slack'
}

type SSOCheckResponse = {
  readonly methods: AuthenticationMethod[];
  readonly passwordEnabled: boolean;
  readonly samlRequired: boolean;
  readonly userExists?: boolean;
};

type AuthenticationMethod = {
  readonly type: AuthenticationMethodsType;
  readonly redirect_to: string;
};

export type AuthenticationMethodsCheckOptions = {
  readonly email: string;
  readonly allowAuthenticationsMethodCheck?: boolean;
};

const getAuthenticationMethod = (
  authenticationMethods: SSOCheckResponse
): AuthenticationMethod | undefined => {
  if (authenticationMethods?.userExists !== undefined) {
    // TODO: delete this block when we are happy with changes from backend
    if (
      !authenticationMethods?.methods ||
      authenticationMethods?.methods?.length === 0
    ) {
      return undefined;
    }
    const samlMethod = authenticationMethods.methods.find(
      (method: AuthenticationMethod) => {
        return (
          method.type === AuthenticationMethodsType.SAML ||
          method.type === AuthenticationMethodsType.GOOGLE
        );
      }
    );
    const passwordMethod = authenticationMethods.methods.find(
      (method: AuthenticationMethod) => {
        return method.type === AuthenticationMethodsType.PASSWORD;
      }
    );
    const userExists = authenticationMethods.userExists;

    if (userExists && passwordMethod) {
      return passwordMethod;
    }
    if (!passwordMethod && samlMethod) {
      return samlMethod;
    } else {
      return undefined;
    }
  } else {
    if (!authenticationMethods?.methods) {
      return undefined;
    } else {
      const { passwordEnabled, samlRequired } = authenticationMethods;

      if (authenticationMethods?.methods?.length === 0) {
        return undefined;
      }
      if (passwordEnabled && samlRequired) {
        const password = authenticationMethods.methods.find(
          (method: AuthenticationMethod) => {
            return method.type === AuthenticationMethodsType.PASSWORD;
          }
        );
        return password;
      }
      if (passwordEnabled && !samlRequired) {
        const password = authenticationMethods.methods.find(
          (method: AuthenticationMethod) => {
            return method.type === AuthenticationMethodsType.PASSWORD;
          }
        );
        return password;
      }

      if (!passwordEnabled && samlRequired) {
        const saml = authenticationMethods.methods.find(
          (method: AuthenticationMethod) => {
            return method.type === AuthenticationMethodsType.SAML;
          }
        );
        return saml;
      } else {
        return undefined;
      }
    }
  }
};

export function useAuthenticationMethodsCheck({
  email,
  allowAuthenticationsMethodCheck
}: AuthenticationMethodsCheckOptions): SSOCheck {
  const [{ isCheckingSSO, ssoRedirectUrl, isUserExists }, setSSOValidation] =
    useState<SSOCheck>({
      isCheckingSSO: false,
      ssoRedirectUrl: '',
      isUserExists: false
    });

  const stargateClient = useStargateClient();

  const getSSORedirectUrl = async (email: string) => {
    setSSOValidation({ isCheckingSSO: true, ssoRedirectUrl: '' });
    try {
      const response: any =
        await stargateClient.checkAuthenticationMethodsAvailable({
          email,
          successRedirect: window.location.href,
          webPlatform: 'magnolia'
        });
      const authenticationMethod = getAuthenticationMethod(response);
      //TODO: remove use of userExists when we are happy with the backend changes
      const userExists = response?.userExists;
      if (authenticationMethod?.type === AuthenticationMethodsType.PASSWORD) {
        if (!!userExists) {
          setSSOValidation({
            isCheckingSSO: false,
            ssoRedirectUrl: 'exists',
            isUserExists: userExists // need to remove in the future
          });
        } else {
          // temporary fix till identity gets us the right url, then just pass the redirect_to
          const url = authenticationMethod.redirect_to.replace(
            '/initiate/password',
            ''
          );
          setSSOValidation({
            isCheckingSSO: false,
            ssoRedirectUrl: url,
            isUserExists: false // need to remove in the future
          });
        }
      } else if (
        authenticationMethod?.type === AuthenticationMethodsType.SAML
      ) {
        if (!!userExists) {
          setSSOValidation({
            isCheckingSSO: false,
            ssoRedirectUrl: authenticationMethod.redirect_to,
            isUserExists: userExists // need to remove in the future
          });
        } else {
          setSSOValidation({
            isCheckingSSO: false,
            ssoRedirectUrl: authenticationMethod.redirect_to,
            isUserExists: false // need to remove in the future
          });
        }
      } else {
        setSSOValidation({
          isCheckingSSO: false,
          ssoRedirectUrl: '',
          isUserExists: false // need to remove in the future
        });
      }
    } catch (error: any) {
      metalClient.error.submit({
        name: catalog.error.INPUT,
        page: 'sign-up',
        component: 'bxpkit-wac-sign-up-input-email',
        error: { ...error }
      });
      setSSOValidation({
        isCheckingSSO: false,
        ssoRedirectUrl: 'error',
        isUserExists: true
      });
    }
  };

  useEffect(() => {
    if (allowAuthenticationsMethodCheck) {
      void getSSORedirectUrl(email);
    }
  }, [email, allowAuthenticationsMethodCheck]);

  return {
    isCheckingSSO,
    ssoRedirectUrl,
    isUserExists
  };
}
